import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import EventCard from '../components/EventCard';
import EventDetail from '../components/EventDetail';
import Banner from '../components/Banner';
import FeaturedEventsCarousel from '../components/FeaturedEventsCarousel';
// import PastVybesCarousel from '../components/PastVybesCarousel'; // Temporarily hidden
import WelcomeToTheTribeCarousel from '../components/WelcomeToTheTribeCarousel';
import FilterMenu, { FilterState } from '../components/FilterMenu';
import { FilterChip } from '../components/ui/FilterChip';
import FilterIconButton from '../components/ui/FilterIconButton';
import SortIconButton from '../components/ui/SortIconButton';
import { Event, BannerConfig } from '../types';
import { getAllEvents, getFeaturedEvents, getEventsCount } from '../services/eventService';
import { PlusCircle, Filter, Shield, Sparkles, HelpCircle, Map as MapIcon, List, ArrowUp, Download, Users } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import CreateEventModal from '../components/CreateEventModal';
import VybeCounterModal from '../components/VybeCounterModal';
import { canCreateVybe } from '../utils/vybeLimits';
import VipLaunchBanner from '../components/VipLaunchBanner';
import ProfileCompletionBanner from '../components/ProfileCompletionBanner';
import SwipeEducationModal from '../components/SwipeEducationModal';
import { buildGates } from '../utils/gates';
import { getHiddenVybeIds, getLocalHiddenVybes } from '../services/hiddenVybesService';
import { EventCardSkeleton } from '../components/ui/SkeletonLoader';
import EmptyState from '../components/ui/EmptyState';
import { filterOutAutoHiddenEvents } from '../utils/eventFilter';
import ExploreMapView from '../components/ExploreMapView';
import { trackPageView, trackEvent } from '../services/analyticsService';
import { trackPageView as trackBehaviorPageView, trackButtonClick } from '../services/behaviorTracking';
import PWAInstallBanner from '../components/PWAInstallBanner';
import IOSInstallModal from '../components/IOSInstallModal';
import { isRunningAsApp } from '../utils/appMode';
import { initPWAInstall, canInstallPWA, isIOS, showInstallPrompt, onInstallAvailableChange } from '../utils/pwaInstall';

// Hero image from Unsplash
const heroImage = 'https://images.unsplash.com/photo-1619537903549-0981d6bca911?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D';
const heroAlt = 'Friends enjoying time together at a social gathering';

// Use the same logo as LandingPage
import heroLogo from '../assets/hero/logo.png';

const EventsPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, isAuthenticated, sessionChecked, isLoading, isVerified } = useAuth();

  // Declare all state hooks before any conditional returns
  const [events, setEvents] = useState<Event[]>([]);
  const [featuredEvents, setFeaturedEvents] = useState<Event[]>([]);
  const [pastVybes, setPastVybes] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [isEventsLoading, setIsEventsLoading] = useState(true);
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [showVybeCounterModal, setShowVybeCounterModal] = useState(false);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showSwipeHelp, setShowSwipeHelp] = useState(false);
  const [userApplications, setUserApplications] = useState<Map<string, string>>(new Map());
  const [activeFilters, setActiveFilters] = useState<FilterState>({
    nearbyOnly: false,
    maxDistance: 50,
    dateFrom: '',
    dateTo: '',
    category: '',
    favoritesOnly: false,
  });
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [sortBy, setSortBy] = useState<'date-asc' | 'date-desc' | 'distance' | 'category'>('date-asc');
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [vybeTypeFilter, setVybeTypeFilter] = useState<{ community: boolean; hosted: boolean }>({
    community: true,
    hosted: true,
  });
  const [showImageDisclaimer, setShowImageDisclaimer] = useState(false);
  const [showVybeTypeTooltip, setShowVybeTypeTooltip] = useState(false);

  // PWA install state
  const [canInstall, setCanInstall] = useState(canInstallPWA());
  const [runningAsApp, setRunningAsApp] = useState(isRunningAsApp());
  const [showIOSInstallModal, setShowIOSInstallModal] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const PAGE_SIZE = 20;

  // Infinite scroll and back to top
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const isLoadingRef = useRef(false);

  // Lock body scroll when sort menu is open
  useEffect(() => {
    if (showSortMenu) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showSortMenu]);

  // Infinite scroll observer
  useEffect(() => {
    if (!loadMoreTriggerRef.current || !hasMore || isLoadingMore || viewMode === 'map') return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          loadMoreEvents();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    const currentTrigger = loadMoreTriggerRef.current;
    observer.observe(currentTrigger);

    return () => {
      if (currentTrigger) {
        observer.unobserve(currentTrigger);
      }
      observer.disconnect();
    };
  }, [hasMore, isLoadingMore, viewMode, filteredEvents.length]);

  // Back to top button visibility (throttled for performance)
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setShowBackToTop(window.scrollY > 400);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Initialize PWA install functionality
  useEffect(() => {
    initPWAInstall();

    const handleInstallChange = (available: boolean) => {
      setCanInstall(available);
    };

    const unsubscribe = onInstallAvailableChange(handleInstallChange);
    return () => unsubscribe();
  }, []);

  // Handle PWA install button click
  const handleInstallApp = async () => {
    trackEvent('install_app_clicked', { source: 'explore_page' });

    // iOS: Show instructions
    if (isIOS()) {
      setShowIOSInstallModal(true);
      return;
    }

    // Android/Desktop: Show native prompt
    const installed = await showInstallPrompt();
    if (installed) {
      setCanInstall(false);
      setRunningAsApp(true);
      toast.success('App installed successfully!');
    }
  };

  // Load initial events with pagination
  useEffect(() => {
    const loadEvents = async () => {
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;
      setIsEventsLoading(true);

      // Track page view (only on first mount, not when filter changes)
      if (page === 0) {
        trackPageView('explore', { user_id: currentUser?.id }, currentUser?.id);
        trackBehaviorPageView('ExplorePage', '/vybes');
      }

      // Initialize PWA install listener
      initPWAInstall();

      try {
        // Load user applications if authenticated
        if (isAuthenticated && currentUser?.id) {
          const { supabase } = await import('../lib/supabaseClient');
          const { data: applications } = await supabase
            .from('event_applications')
            .select('event_id, status')
            .eq('applicant_id', currentUser.id);

          if (applications) {
            const appMap = new Map<string, string>();
            applications.forEach((app: any) => {
              appMap.set(app.event_id, app.status);
            });
            setUserApplications(appMap);
          }
        }

        // Determine vybe type filter
        let vybeType: 'community' | 'hosted' | 'all' = 'all';
        if (vybeTypeFilter.community && !vybeTypeFilter.hosted) {
          vybeType = 'community';
        } else if (vybeTypeFilter.hosted && !vybeTypeFilter.community) {
          vybeType = 'hosted';
        }

        // Get total count with filter
        const count = await getEventsCount(
          vybeType,
          activeFilters.category || undefined,
          activeFilters.dateFrom || undefined,
          activeFilters.dateTo || undefined
        );
        setTotalCount(count);

        // Load first page with filter
        const allEvents = await getAllEvents(currentUser?.id, {
          limit: PAGE_SIZE,
          offset: 0,
          vybeType,
          category: activeFilters.category || undefined,
          dateFrom: activeFilters.dateFrom || undefined,
          dateTo: activeFilters.dateTo || undefined,
        });

        // Filter out hidden vybes
        let hiddenIds: Set<string>;
        if (isAuthenticated && currentUser?.id) {
          hiddenIds = await getHiddenVybeIds(currentUser.id);
        } else {
          hiddenIds = getLocalHiddenVybes();
        }

        // Filter out manually hidden and auto-hidden (past/full)
        let visibleEvents = filterOutAutoHiddenEvents(
          allEvents.filter(e => !hiddenIds.has(e.id))
        );

        // If authenticated, also filter out accepted events
        if (isAuthenticated && currentUser?.id) {
          const { supabase } = await import('../lib/supabaseClient');
          const { data: acceptedApps } = await supabase
            .from('event_applications')
            .select('event_id')
            .eq('applicant_id', currentUser.id)
            .eq('status', 'accepted');

          if (acceptedApps) {
            const acceptedEventIds = new Set(acceptedApps.map((app: any) => app.event_id));
            visibleEvents = visibleEvents.filter(e => !acceptedEventIds.has(e.id));
          }
        }

        // Apply client-side filters (location and favorites)
        let filtered = visibleEvents;

        // Filter by location if enabled
        if (activeFilters.nearbyOnly && currentUser?.latitude && currentUser?.longitude && activeFilters.maxDistance < 500) {
          filtered = filtered.filter(event => {
            if (!event.latitude || !event.longitude) return false;
            const distance = calculateDistance(
              currentUser.latitude!,
              currentUser.longitude!,
              event.latitude,
              event.longitude
            );
            return distance <= activeFilters.maxDistance;
          });
        }

        // Filter by favorites
        if (activeFilters.favoritesOnly) {
          if (currentUser?.favorites && currentUser.favorites.length > 0) {
            filtered = filtered.filter(event => currentUser.favorites!.includes(event.id));
          } else {
            filtered = [];
          }
        }

        setEvents(visibleEvents);
        setFilteredEvents(filtered);
        setHasMore(allEvents.length >= PAGE_SIZE);
        setPage(0);

        const featured = await getFeaturedEvents();
        const visibleFeatured = filterOutAutoHiddenEvents(
          featured.filter(e => !hiddenIds.has(e.id))
        );
        setFeaturedEvents(visibleFeatured);

        // Load past vybes (show more cancelled/past events for social proof)
        const { getPastVybes } = await import('../services/eventService');
        const pastEvents = await getPastVybes(15);

        console.log('Loaded past vybes:', pastEvents.length);
        if (pastEvents && pastEvents.length > 0) {
          setPastVybes(pastEvents);
        }
      } catch (error) {
        console.error('Error loading events:', error);
      } finally {
        setIsEventsLoading(false);
        isLoadingRef.current = false;
      }
    };
    loadEvents();
  }, [currentUser?.id, isAuthenticated, vybeTypeFilter, activeFilters]);

  // Load more events function
  const loadMoreEvents = async () => {
    if (!hasMore || isLoadingMore) return;

    try {
      setIsLoadingMore(true);
      const nextPage = page + 1;
      const offset = nextPage * PAGE_SIZE;

      // Determine vybe type filter
      let vybeType: 'community' | 'hosted' | 'all' = 'all';
      if (vybeTypeFilter.community && !vybeTypeFilter.hosted) {
        vybeType = 'community';
      } else if (vybeTypeFilter.hosted && !vybeTypeFilter.community) {
        vybeType = 'hosted';
      }

      const moreEvents = await getAllEvents(currentUser?.id, {
        limit: PAGE_SIZE,
        offset,
        vybeType,
        category: activeFilters.category || undefined,
        dateFrom: activeFilters.dateFrom || undefined,
        dateTo: activeFilters.dateTo || undefined,
      });

      // Filter out hidden vybes
      let hiddenIds: Set<string>;
      if (isAuthenticated && currentUser?.id) {
        hiddenIds = await getHiddenVybeIds(currentUser.id);
      } else {
        hiddenIds = getLocalHiddenVybes();
      }

      let visibleNewEvents = filterOutAutoHiddenEvents(
        moreEvents.filter(e => !hiddenIds.has(e.id))
      );

      // Filter out accepted events if authenticated
      if (isAuthenticated && currentUser?.id) {
        const { supabase } = await import('../lib/supabaseClient');
        const { data: acceptedApps } = await supabase
          .from('event_applications')
          .select('event_id')
          .eq('applicant_id', currentUser.id)
          .eq('status', 'accepted');

        if (acceptedApps) {
          const acceptedEventIds = new Set(acceptedApps.map((app: any) => app.event_id));
          visibleNewEvents = visibleNewEvents.filter(e => !acceptedEventIds.has(e.id));
        }
      }

      if (visibleNewEvents.length > 0) {
        // Apply client-side filters to new events
        let filtered = visibleNewEvents;

        // Filter by location if enabled
        if (activeFilters.nearbyOnly && currentUser?.latitude && currentUser?.longitude && activeFilters.maxDistance < 500) {
          filtered = filtered.filter(event => {
            if (!event.latitude || !event.longitude) return false;
            const distance = calculateDistance(
              currentUser.latitude!,
              currentUser.longitude!,
              event.latitude,
              event.longitude
            );
            return distance <= activeFilters.maxDistance;
          });
        }

        // Filter by favorites
        if (activeFilters.favoritesOnly) {
          if (currentUser?.favorites && currentUser.favorites.length > 0) {
            filtered = filtered.filter(event => currentUser.favorites!.includes(event.id));
          } else {
            filtered = [];
          }
        }

        setEvents(prev => [...prev, ...visibleNewEvents]);
        setFilteredEvents(prev => [...prev, ...filtered]);
        setPage(nextPage);
      }

      setHasMore(moreEvents.length >= PAGE_SIZE);
    } catch (error) {
      console.error('Error loading more events:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Memoize apply filters using useCallback
  // Note: Most filters (category, date) are now applied at DB level
  // This function now just sets the filters and lets the useEffect reload data
  const applyFilters = useCallback(async (filters: FilterState) => {
    setActiveFilters(filters);
    // Reset pagination when filters change
    setPage(0);
    setHasMore(true);
  }, []);

  const refreshEvents = async () => {
    try {
      setIsEventsLoading(true);
      const allEvents = await getAllEvents(currentUser?.id, { limit: PAGE_SIZE, offset: 0 });
      setEvents(allEvents);
      setFilteredEvents(allEvents);
      setPage(0);
      setHasMore(allEvents.length >= PAGE_SIZE);
    } catch (e) {
      console.error('Error refreshing events:', e);
    } finally {
      setIsEventsLoading(false);
    }
  };

  // Helper function to calculate distance between two coordinates
  const calculateDistance = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number => {
    const R = 3959; // Earth's radius in miles
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Memoize sorted events to prevent unnecessary re-sorting
  const sortedFilteredEvents = useMemo(() => {
    // No need to filter by vybe type here - it's done at the database level
    let filtered = [...filteredEvents];

    switch (sortBy) {
      case 'date-asc':
        return filtered.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      case 'date-desc':
        return filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      case 'distance':
        if (currentUser?.latitude && currentUser?.longitude) {
          return filtered.sort((a, b) => {
            const distA = a.latitude && a.longitude
              ? calculateDistance(currentUser.latitude!, currentUser.longitude!, a.latitude, a.longitude)
              : Infinity;
            const distB = b.latitude && b.longitude
              ? calculateDistance(currentUser.latitude!, currentUser.longitude!, b.latitude, b.longitude)
              : Infinity;
            return distA - distB;
          });
        }
        return filtered;

      case 'category':
        return filtered.sort((a, b) => a.category.localeCompare(b.category));

      default:
        return filtered;
    }
  }, [filteredEvents, sortBy, currentUser?.latitude, currentUser?.longitude]);

  const hasActiveFilters =
    activeFilters.nearbyOnly ||
    activeFilters.dateFrom !== '' ||
    activeFilters.dateTo !== '' ||
    activeFilters.category !== '';

  const activeFilterCount = [
    activeFilters.nearbyOnly,
    activeFilters.dateFrom !== '',
    activeFilters.dateTo !== '',
    activeFilters.category !== '',
    activeFilters.favoritesOnly
  ].filter(Boolean).length;

  const handleEventClick = useCallback((event: Event) => {
    setSelectedEvent(event);
    window.scrollTo(0, 0);
  }, []);

  const handleCreateNewEvent = async () => {
    trackButtonClick('CreateVybeButton', 'conversion', { page: 'explore' });

    if (!isVerified()) {
      navigate('/verify');
      return;
    }
    if (!currentUser) return;

    const isSubscribed = currentUser.subscriptionTier === 'subscribed';
    const { canCreate } = await canCreateVybe(currentUser.id, isSubscribed);

    if (!canCreate) {
      setShowVybeCounterModal(true);
      return;
    }

    setShowCreateEventModal(true);
  };

  // Loading guard (only while auth is hydrating)
  if (!sessionChecked || isLoading) {
    return (
      <div className="container mx-auto p-4">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-secondary"></div>
        </div>
      </div>
    );
  }

  const bannerConfig: BannerConfig = {
    bannerImage: heroImage,
    bannerImageMobile: heroImage,
    bannerAlt: heroAlt,
    title: '',
    subtitle: '',
    textColor: 'text-contrast',
    overlayColor: 'from-primary',
    overlayOpacity: 70,
    customContent: (
      <div className="relative z-10 flex justify-center">
        <img
          src={heroLogo}
          alt="VybeTribe"
          className="w-40 md:w-64 lg:w-72 h-auto drop-shadow-[0_6px_28px_rgba(0,0,0,0.55)]"
        />
      </div>
    ),
  };

  // Get gates for VIP banner
  const gates = buildGates(currentUser);

  // Check if user can view events (browsing always allowed)
  const viewEventsGate = gates.canViewEvents();
  if (!viewEventsGate.allowed) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto bg-gradient-to-br from-indigo-800 via-purple-800 to-indigo-900 rounded-2xl p-8 border border-purple-700 shadow-card-glow text-center">
          <div className="mb-6">
            <div className="w-20 h-20 bg-purple-600/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">{viewEventsGate.title}</h2>
            <p className="text-white/80 text-lg mb-6">{viewEventsGate.message}</p>
          </div>
          <button
            onClick={() => navigate(viewEventsGate.actionUrl || '/verify')}
            className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors shadow-lg text-lg"
          >
            {viewEventsGate.actionText || 'Get Verified'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {selectedEvent ? (
        <div className="container mx-auto px-2 sm:px-4">
          <EventDetail event={selectedEvent} onBack={() => setSelectedEvent(null)} onApplied={refreshEvents} />
        </div>
      ) : (
        <>
          <Banner config={bannerConfig} />

          <div className="container mx-auto px-2 sm:px-4 py-8">
            {/* PWA Install Banner - Prominent placement for all users */}
            <PWAInstallBanner />

            {/* VIP Launch Banner */}
            {gates.vip.active && gates.vip.endsAt && (
              <VipLaunchBanner
                endsAt={gates.vip.endsAt}
                userId={currentUser?.id}
                isEmailVerified={currentUser?.email_verified ?? false}
              />
            )}

            {/* Profile Completion Banner */}
            <ProfileCompletionBanner user={currentUser} />

            {/* Get the app button - only for logged out users, in browser */}
            {!isAuthenticated && !runningAsApp && canInstall && (
              <div className="mb-4 flex justify-center">
                <div className="p-[2px] rounded-full bg-gradient-to-r from-[#FF3148] to-[#00D1FF]">
                  <button
                    onClick={handleInstallApp}
                    className="flex items-center gap-2 text-white/80 hover:text-white px-6 py-2.5 rounded-full bg-theme-background transition-all font-medium text-sm"
                    aria-label="Get the app"
                  >
                    <Download size={16} />
                    Get the app (recommended)
                  </button>
                </div>
              </div>
            )}

            {/* Fresh Content Banner */}
            <div className="mb-6 px-3 py-2.5 bg-white/5 rounded-lg border border-white/10">
              <div className="flex items-center justify-center gap-2">
                <Sparkles size={14} className="text-cyan-400/80" />
                <p className="text-sm font-medium text-theme-text-secondary">
                  New Vybes added daily
                </p>
                <Sparkles size={14} className="text-cyan-400/80" />
              </div>
            </div>

            {/* Conditional: Welcome to the Tribe (logged in) OR Recent Vybes (logged out) */}
            {isAuthenticated ? (
              // Logged in users see the Tribe carousel
              <div className="mb-16">
                <div className="mb-5">
                  <div className="flex items-center mb-1.5">
                    <div className="w-2 h-8 vybe-gradient-accent rounded-full mr-3"></div>
                    <h2 className="text-2xl font-bold text-contrast">Welcome to the Tribe</h2>
                  </div>
                  <p className="text-xs text-theme-text-tertiary pl-5 opacity-70">Meet some of the amazing people in our Tribe.</p>
                </div>
                <WelcomeToTheTribeCarousel />
              </div>
            ) : (
              // Logged out users see recent vybes
              featuredEvents.length > 0 && (
                <div className="mb-16">
                  <div className="mb-5">
                    <div className="flex items-center mb-1.5">
                      <div className="w-2 h-8 vybe-gradient-accent rounded-full mr-3"></div>
                      <h2 className="text-2xl font-bold text-contrast">Recent Vybes</h2>
                    </div>
                    <p className="text-xs text-theme-text-tertiary pl-5 opacity-70">See what people are gathering for.</p>
                  </div>
                  <FeaturedEventsCarousel events={featuredEvents.slice(0, 8)} onEventClick={handleEventClick} />
                </div>
              )
            )}

            {/* Past Vybes Carousel - Temporarily hidden */}
            {/* {pastVybes.length > 0 && (
              <div className="mb-16">
                <div className="mb-5">
                  <div className="flex items-center mb-1.5">
                    <div className="w-2 h-8 vybe-gradient-accent rounded-full mr-3"></div>
                    <h2 className="text-2xl font-bold text-contrast">Past Vybes</h2>
                  </div>
                  <p className="text-xs text-theme-text-tertiary pl-5 opacity-70">A snapshot of Vybes that have already taken place.</p>
                </div>
                <PastVybesCarousel
                  events={pastVybes}
                  onEventUpdate={async () => {
                    const { getPastVybes } = await import('../services/eventService');
                    const refreshedEvents = await getPastVybes(15);
                    setPastVybes(refreshedEvents);
                  }}
                />
              </div>
            )} */}

            <div className="mb-10 space-y-4">
              {/* Desktop: Three-section layout */}
              <div className="hidden md:flex items-center gap-5 relative">
                {/* Left: Sort, Filter, Host Vybe or Join CTA */}
                <div className="flex gap-3 items-center flex-1">
                  {/* Only show sort and filter buttons for authenticated users */}
                  {isAuthenticated && (
                    <>
                      <div className="relative">
                        <SortIconButton
                          active={showSortMenu || sortBy !== 'date-asc'}
                          onClick={() => setShowSortMenu(!showSortMenu)}
                        />
                        {showSortMenu && (
                          <>
                            <div
                              className="fixed inset-0 z-40"
                              onClick={() => setShowSortMenu(false)}
                            />
                            <div className="absolute left-0 top-full mt-2 bg-theme-cards backdrop-blur-md rounded-xl shadow-lg border border-white/10 py-2 z-50 min-w-[200px]">
                              {[
                                { value: 'date-asc', label: 'Date (Soonest First)' },
                                { value: 'date-desc', label: 'Date (Latest First)' },
                                { value: 'distance', label: 'By Distance' },
                                { value: 'category', label: 'By Type' }
                              ].map((option) => (
                                <button
                                  key={option.value}
                                  onClick={() => {
                                    setSortBy(option.value as 'date-asc' | 'date-desc' | 'distance' | 'category');
                                    setShowSortMenu(false);
                                  }}
                                  className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                                    sortBy === option.value
                                      ? 'bg-gradient-to-r from-pink-500/20 to-cyan-500/20 text-white font-medium'
                                      : 'text-white/80 hover:text-white hover:bg-white/5'
                                  }`}
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                      <div className="relative">
                        <FilterIconButton
                          onClick={() => setShowFilterMenu(true)}
                          active={hasActiveFilters}
                        />
                        {activeFilterCount > 0 && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-pink-500 to-cyan-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                            {activeFilterCount}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Host Vybe button or Join CTA - desktop only */}
                  {isAuthenticated ? (
                    isVerified() ? (
                      <button
                        onClick={handleCreateNewEvent}
                        className="bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white px-6 py-2.5 rounded-full transition font-semibold shadow-lg text-base inline-flex items-center justify-center"
                      >
                        <PlusCircle size={18} className="mr-2" />
                        <span>Host Vybe</span>
                      </button>
                    ) : (
                      <button
                        onClick={() => navigate('/verify')}
                        className="bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white px-6 py-2.5 rounded-full transition font-semibold shadow-lg text-base inline-flex items-center justify-center whitespace-nowrap"
                      >
                        <Shield size={18} className="mr-2" />
                        <span>Verify to Host</span>
                      </button>
                    )
                  ) : (
                    <div className="relative p-[1.5px] rounded-full bg-gradient-to-r from-pink-500/60 to-cyan-500/60">
                      <button
                        onClick={() => {
                          trackButtonClick('signup_cta_banner', 'events_page');
                          navigate('/login?signup=true&returnUrl=/vybes');
                        }}
                        className="px-5 py-2 text-sm font-medium text-white bg-primary-100 rounded-full hover:bg-primary-100/80 transition inline-flex items-center justify-center"
                      >
                        <span>Join free</span>
                      </button>
                    </div>
                  )}
                </div>

                {/* Center: Community/Hosted Filter Toggle - Absolutely centered */}
                <div className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
                  <p className="text-xs text-theme-text-secondary font-medium">Show Vybes from</p>
                  <div className="flex items-center bg-theme-bg-secondary rounded-lg border border-theme-border-primary p-1">
                    <button
                      onClick={() => setVybeTypeFilter(prev => ({ ...prev, community: !prev.community }))}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-md transition-all ${
                        vybeTypeFilter.community
                          ? 'bg-gradient-to-r from-pink-500 to-cyan-500 text-white'
                          : 'text-theme-text-secondary hover:text-theme-text-main'
                      }`}
                    >
                      <Sparkles size={16} />
                      <span className="text-sm font-medium">Community</span>
                    </button>
                    <button
                      onClick={() => setVybeTypeFilter(prev => ({ ...prev, hosted: !prev.hosted }))}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-md transition-all ${
                        vybeTypeFilter.hosted
                          ? 'bg-gradient-to-r from-pink-500 to-cyan-500 text-white'
                          : 'text-theme-text-secondary hover:text-theme-text-main'
                      }`}
                    >
                      <Users size={16} />
                      <span className="text-sm font-medium">Hosted</span>
                    </button>
                  </div>
                  <div className="relative">
                    <button
                      onClick={() => setShowVybeTypeTooltip(!showVybeTypeTooltip)}
                      onMouseEnter={() => setShowVybeTypeTooltip(true)}
                      onMouseLeave={() => setShowVybeTypeTooltip(false)}
                      className="text-theme-text-tertiary hover:text-theme-text-secondary transition-colors"
                      aria-label="Learn about vybe types"
                    >
                      <HelpCircle size={18} />
                    </button>
                    {showVybeTypeTooltip && (
                      <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-72 bg-theme-cards backdrop-blur-md rounded-lg shadow-lg border border-white/10 p-4 z-50">
                        <div className="space-y-3 text-sm">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Sparkles size={14} className="text-pink-400" />
                              <span className="font-semibold text-white">Community Vybes</span>
                            </div>
                            <p className="text-theme-text-secondary">Suggested gatherings from our community matching engine</p>
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Users size={14} className="text-cyan-400" />
                              <span className="font-semibold text-white">Hosted Vybes</span>
                            </div>
                            <p className="text-theme-text-secondary">Events created by verified community members</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right: List/Map Toggle */}
                <div className="flex items-center bg-theme-bg-secondary rounded-lg border border-theme-border-primary p-1 ml-auto">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all ${
                      viewMode === 'list'
                        ? 'bg-gradient-to-r from-pink-500 to-cyan-500 text-white'
                        : 'text-theme-text-secondary hover:text-theme-text-main'
                    }`}
                  >
                    <List size={16} />
                    <span className="text-sm font-medium">List</span>
                  </button>
                  <button
                    onClick={() => setViewMode('map')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all ${
                      viewMode === 'map'
                        ? 'bg-gradient-to-r from-pink-500 to-cyan-500 text-white'
                        : 'text-theme-text-secondary hover:text-theme-text-main'
                    }`}
                  >
                    <MapIcon size={16} />
                    <span className="text-sm font-medium">Map</span>
                  </button>
                </div>
              </div>

              {/* Mobile: Stacked layout */}
              <div className="flex md:hidden flex-col gap-2">
                {/* Join CTA for logged-out users */}
                {!isAuthenticated && (
                  <div className="relative p-[1.5px] rounded-lg bg-gradient-to-r from-pink-500/50 to-cyan-500/50 mb-0.5">
                    <button
                      onClick={() => {
                        trackButtonClick('signup_cta_banner', 'events_page');
                        navigate('/login?signup=true&returnUrl=/vybes');
                      }}
                      className="w-full px-4 py-1.5 text-sm font-medium text-white bg-primary-100 rounded-lg hover:bg-primary-100/80 transition"
                    >
                      Join free
                    </button>
                  </div>
                )}

                {/* Label above Community/Hosted Toggle */}
                <p className="text-xs text-theme-text-secondary font-medium">Show Vybes from</p>

                {/* Row 1: Community/Hosted Toggle (full width, compact) */}
                <div className="flex items-center bg-theme-bg-secondary/50 rounded-lg border border-theme-border-primary/40 p-0.5 -mt-2">
                  <button
                    onClick={() => setVybeTypeFilter(prev => ({ ...prev, community: !prev.community }))}
                    className={`flex-1 flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-md transition-all ${
                      vybeTypeFilter.community
                        ? 'bg-gradient-to-r from-pink-500/30 to-cyan-500/30 text-white/85'
                        : 'text-theme-text-secondary/70 hover:text-theme-text-main'
                    }`}
                  >
                    <Sparkles size={14} className={vybeTypeFilter.community ? 'opacity-75' : 'opacity-60'} />
                    <span className="text-xs font-medium">Community</span>
                  </button>
                  <button
                    onClick={() => setVybeTypeFilter(prev => ({ ...prev, hosted: !prev.hosted }))}
                    className={`flex-1 flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-md transition-all ${
                      vybeTypeFilter.hosted
                        ? 'bg-gradient-to-r from-pink-500/30 to-cyan-500/30 text-white/85'
                        : 'text-theme-text-secondary/70 hover:text-theme-text-main'
                    }`}
                  >
                    <Users size={14} className={vybeTypeFilter.hosted ? 'opacity-75' : 'opacity-60'} />
                    <span className="text-xs font-medium">Hosted</span>
                  </button>
                </div>

                {/* Row 2: List/Map Toggle (full width, compact) */}
                <div className="flex items-center bg-theme-bg-secondary/50 rounded-lg border border-theme-border-primary/40 p-0.5">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`flex-1 flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-md transition-all ${
                      viewMode === 'list'
                        ? 'bg-gradient-to-r from-pink-500/30 to-cyan-500/30 text-white/85'
                        : 'text-theme-text-secondary/70 hover:text-theme-text-main'
                    }`}
                  >
                    <List size={14} className={viewMode === 'list' ? 'opacity-75' : 'opacity-60'} />
                    <span className="text-xs font-medium">List</span>
                  </button>
                  <button
                    onClick={() => setViewMode('map')}
                    className={`flex-1 flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-md transition-all ${
                      viewMode === 'map'
                        ? 'bg-gradient-to-r from-pink-500/30 to-cyan-500/30 text-white/85'
                        : 'text-theme-text-secondary/70 hover:text-theme-text-main'
                    }`}
                  >
                    <MapIcon size={14} className={viewMode === 'map' ? 'opacity-75' : 'opacity-60'} />
                    <span className="text-xs font-medium">Map</span>
                  </button>
                </div>
              </div>
            </div>


            {/* Active Filter Chips */}
            {hasActiveFilters && (
              <div className="flex flex-wrap gap-2 mb-6 items-center">
                {activeFilters.favoritesOnly && (
                  <FilterChip
                    label="Favourites"
                    onClear={() => {
                      const newFilters = { ...activeFilters, favoritesOnly: false };
                      setActiveFilters(newFilters);
                      applyFilters(newFilters);
                    }}
                  />
                )}
                {activeFilters.nearbyOnly && (
                  <FilterChip
                    label={activeFilters.maxDistance >= 500 ? 'National' : `Within ${activeFilters.maxDistance} mi`}
                    onClear={() => {
                      const newFilters = { ...activeFilters, nearbyOnly: false };
                      setActiveFilters(newFilters);
                      applyFilters(newFilters);
                    }}
                  />
                )}
                {activeFilters.dateFrom && (
                  <FilterChip
                    label={`From ${new Date(activeFilters.dateFrom).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
                    onClear={() => {
                      const newFilters = { ...activeFilters, dateFrom: '' };
                      setActiveFilters(newFilters);
                      applyFilters(newFilters);
                    }}
                  />
                )}
                {activeFilters.dateTo && (
                  <FilterChip
                    label={`To ${new Date(activeFilters.dateTo).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
                    onClear={() => {
                      const newFilters = { ...activeFilters, dateTo: '' };
                      setActiveFilters(newFilters);
                      applyFilters(newFilters);
                    }}
                  />
                )}
                {activeFilters.category && (
                  <FilterChip
                    label={activeFilters.category}
                    onClear={() => {
                      const newFilters = { ...activeFilters, category: '' };
                      setActiveFilters(newFilters);
                      applyFilters(newFilters);
                    }}
                  />
                )}
                <button
                  onClick={() => {
                    const resetFilters: FilterState = {
                      nearbyOnly: false,
                      maxDistance: 50,
                      dateFrom: '',
                      dateTo: '',
                      category: '',
                      favoritesOnly: false,
                    };
                    setActiveFilters(resetFilters);
                    applyFilters(resetFilters);
                  }}
                  className="text-xs text-theme-text-secondary hover:text-white underline transition-colors"
                >
                  Clear all
                </button>
              </div>
            )}

            {isEventsLoading ? (
              <div className="space-y-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 xl:gap-8">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <EventCardSkeleton key={i} />
                  ))}
                </div>
              </div>
            ) : viewMode === 'map' ? (
              <div className="w-full h-[calc(100vh-400px)] min-h-[600px] rounded-2xl overflow-hidden">
                <ExploreMapView
                  events={sortedFilteredEvents}
                  userLocation={
                    currentUser?.latitude && currentUser?.longitude
                      ? { lat: currentUser.latitude, lng: currentUser.longitude }
                      : undefined
                  }
                  currentUserId={currentUser?.id}
                  onEventClick={handleEventClick}
                  acceptedEventIds={[]}
                />
              </div>
            ) : (
              <>
                {/* Featured Vybes - Only visible for authenticated users (logged out users see it at top) */}
                {isAuthenticated && featuredEvents.length > 0 && (
                  <div className="mb-10">
                    <div className="flex items-center mb-5">
                      <div className="w-2 h-8 vybe-gradient-accent rounded-full mr-3"></div>
                      <h2 className="text-2xl font-bold text-contrast">Featured Vybes</h2>
                    </div>
                    <FeaturedEventsCarousel events={featuredEvents} onEventClick={handleEventClick} />
                  </div>
                )}

                <div className="mt-10">
                  {/* Mobile: Sort & Filter controls above All Vybes */}
                  <div className="flex md:hidden items-center gap-2 mb-4">
                    {/* Only show sort and filter for authenticated users */}
                    {isAuthenticated && (
                      <>
                        <div className="relative">
                          <SortIconButton
                            active={showSortMenu || sortBy !== 'date-asc'}
                            onClick={() => setShowSortMenu(!showSortMenu)}
                          />
                          {showSortMenu && (
                            <>
                              <div
                                className="fixed inset-0 z-40"
                                onClick={() => setShowSortMenu(false)}
                              />
                              <div className="absolute left-0 top-full mt-2 bg-theme-cards backdrop-blur-md rounded-xl shadow-lg border border-white/10 py-2 z-50 min-w-[200px]">
                                {[
                                  { value: 'date-asc', label: 'Date (Soonest First)' },
                                  { value: 'date-desc', label: 'Date (Latest First)' },
                                  { value: 'distance', label: 'By Distance' },
                                  { value: 'category', label: 'By Type' }
                                ].map((option) => (
                                  <button
                                    key={option.value}
                                    onClick={() => {
                                      setSortBy(option.value as 'date-asc' | 'date-desc' | 'distance' | 'category');
                                      setShowSortMenu(false);
                                    }}
                                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                                      sortBy === option.value
                                        ? 'bg-gradient-to-r from-pink-500/20 to-cyan-500/20 text-white font-medium'
                                        : 'text-white/80 hover:text-white hover:bg-white/5'
                                    }`}
                                  >
                                    {option.label}
                                  </button>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                        <div className="relative">
                          <FilterIconButton
                            onClick={() => setShowFilterMenu(true)}
                            active={hasActiveFilters}
                          />
                          {activeFilterCount > 0 && (
                            <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-pink-500 to-cyan-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                              {activeFilterCount}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center">
                        <div className="w-2 h-8 vybe-gradient-accent rounded-full mr-3"></div>
                        <h2 className="text-2xl font-bold bg-gradient-to-r from-[#ff3148] to-[#00d1ff] bg-clip-text text-transparent">All Vybes</h2>
                      </div>
                      {sortedFilteredEvents.length > 0 && (
                        <span className="text-sm text-theme-text-secondary">
                          {sortedFilteredEvents.length} {sortedFilteredEvents.length === 1 ? 'Vybe' : 'Vybes'}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-theme-text-tertiary pl-5 opacity-70 mb-3">Upcoming Vybes you can join</p>

                    {/* About Vybe Images - Collapsible */}
                    <div className="pl-5">
                      <button
                        onClick={() => setShowImageDisclaimer(!showImageDisclaimer)}
                        className="flex items-center gap-1.5 text-left text-xs text-theme-text-tertiary hover:text-theme-text-secondary transition-colors opacity-60 hover:opacity-80 mb-1.5"
                      >
                        <HelpCircle size={12} className="flex-shrink-0" />
                        <span>About Vybe images</span>
                      </button>
                      <AnimatePresence>
                        {showImageDisclaimer && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg mb-2">
                              <div className="flex items-start gap-2">
                                <Sparkles size={12} className="text-cyan-400 mt-0.5 flex-shrink-0 opacity-70" />
                                <p className="text-xs text-theme-text-secondary leading-relaxed opacity-80">
                                  Images are automatically selected to represent each Vybe. They're for visual reference only and may not reflect the actual event location or participants.
                                </p>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                  {!vybeTypeFilter.community && !vybeTypeFilter.hosted ? (
                    <EmptyState
                      icon={Filter}
                      title="Select a filter"
                      description="Choose Community, Hosted, or both to see Vybes"
                      actionLabel="Show All Vybes"
                      onAction={() => setVybeTypeFilter({ community: true, hosted: true })}
                    />
                  ) : filteredEvents.length > 0 && sortedFilteredEvents.length > 0 ? (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 xl:gap-8">
                        {sortedFilteredEvents.map((event, index) => (
                          <EventCard
                            key={event.id}
                            event={event}
                            onClick={() => handleEventClick(event)}
                            index={index}
                            enableSwipeHide={isAuthenticated}
                            userApplicationStatus={userApplications.get(event.id) as any || null}
                            onRemoved={(id) => {
                              setFilteredEvents(prev => prev.filter(e => e.id !== id));
                              setEvents(prev => prev.filter(e => e.id !== id));
                            }}
                            onUndo={() => {
                              refreshEvents();
                            }}
                          />
                        ))}
                      </div>

                      {/* Load More Section */}
                      <div className="mt-8 flex flex-col items-center gap-4">
                        {hasMore && (
                          <>
                            <div className="text-center">
                              <p className="text-theme-text-secondary text-sm mb-3">
                                Showing {filteredEvents.length} of {totalCount} Vybes
                              </p>
                              <button
                                onClick={loadMoreEvents}
                                disabled={isLoadingMore}
                                className="bg-gradient-to-r from-pink-500 to-cyan-500 hover:from-pink-600 hover:to-cyan-600 disabled:from-gray-500 disabled:to-gray-600 text-white px-8 py-3 rounded-full font-semibold shadow-lg transition-all hover:scale-105 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 min-h-[44px] inline-flex items-center justify-center gap-2"
                              >
                                {isLoadingMore ? (
                                  <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                                    <span>Loading...</span>
                                  </>
                                ) : (
                                  <>
                                    <span>Load More Vybes</span>
                                    <ArrowUp size={18} className="rotate-180" />
                                  </>
                                )}
                              </button>
                            </div>
                            <div ref={loadMoreTriggerRef} className="h-1"></div>
                          </>
                        )}
                        {!hasMore && filteredEvents.length > 0 && (
                          <p className="text-theme-text-secondary text-sm text-center py-4">
                            You've seen all available Vybes
                          </p>
                        )}
                      </div>
                    </>
                  ) : sortedFilteredEvents.length === 0 && (vybeTypeFilter.community || vybeTypeFilter.hosted) ? (
                    <EmptyState
                      icon={Filter}
                      title="No matches found"
                      description="Try selecting both Community and Hosted Vybes, or adjust your other filters"
                      actionLabel="Show All Types"
                      onAction={() => setVybeTypeFilter({ community: true, hosted: true })}
                    />
                  ) : (
                    <EmptyState
                      icon={hasActiveFilters ? Filter : Sparkles}
                      title={hasActiveFilters ? 'No matches found' : 'No Vybes yet'}
                      description={hasActiveFilters ? 'Try adjusting your filters to see more Vybes' : 'Be the first to create a Vybe and start connecting with your Tribe'}
                      actionLabel={hasActiveFilters ? 'Clear Filters' : isAuthenticated ? 'Create a Vybe' : undefined}
                      onAction={hasActiveFilters ? () => {
                        const resetFilters: FilterState = {
                          nearbyOnly: false,
                          maxDistance: 50,
                          dateFrom: '',
                          dateTo: '',
                          category: '',
                          favoritesOnly: false,
                        };
                        applyFilters(resetFilters);
                      } : isAuthenticated ? () => setShowCreateEventModal(true) : undefined}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </>
      )}

      <CreateEventModal
        isOpen={showCreateEventModal}
        onClose={() => setShowCreateEventModal(false)}
        onEventCreated={refreshEvents}
      />

      <VybeCounterModal
        isOpen={showVybeCounterModal}
        onClose={() => setShowVybeCounterModal(false)}
        onUpgrade={() => {
          setShowVybeCounterModal(false);
          navigate('/account');
        }}
      />

      <FilterMenu
        isOpen={showFilterMenu}
        onClose={() => setShowFilterMenu(false)}
        onApplyFilters={applyFilters}
        currentFilters={activeFilters}
      />

      <SwipeEducationModal onClose={() => {}} />

      <IOSInstallModal
        isOpen={showIOSInstallModal}
        onClose={() => setShowIOSInstallModal(false)}
      />

      {/* Back to Top Button */}
      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            onClick={scrollToTop}
            className="fixed bottom-20 sm:bottom-8 right-4 sm:right-8 z-40 bg-gradient-to-r from-pink-500 to-cyan-500 hover:from-pink-600 hover:to-cyan-600 text-white p-4 rounded-full shadow-elegant transition-all hover:scale-110 active:scale-95"
            aria-label="Back to top"
          >
            <ArrowUp size={24} />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

export default React.memo(EventsPage);
